import React, { useEffect, useMemo } from 'react';
import { Box, Button, Typography } from '@heycater/design-system';
import { useTranslation } from 'react-i18next';

import { EXPERIMENT, isVariationActive } from 'lib/ablyft';
import { Caterer } from 'lib/algolia/model';
import { Pill } from 'qualification/components/PillLabel/Pill';
import QuestionErrors from 'qualification/components/QuestionErrors';
import { SurveyControlsLayout } from 'qualification/components/Survey/SurveyControlsLayout';
import SurveySelection from 'qualification/components/Survey/SurveySelection';
import { useQualification } from 'qualification/context';
import { QUALIFICATION_STATUS } from 'qualification/context/model';
import { useBreakpointDown } from 'qualification/hooks/useBreakpoints';
import { Step } from 'qualification/schema/model';
import ArrowBack from 'shared/components/ArrowBack';
import ArrowForward from 'shared/components/ArrowForward';
import useKeyPress from 'shared/hooks/useKeyPress';

interface Props {
  step: Step;
  caterer?: Caterer;
}

function SurveyControls({ step, caterer }: Props) {
  const { t } = useTranslation('qualification');

  const mobile = useBreakpointDown('xs');
  const { state, actions, errorsOfStep, canContinue } = useQualification();
  const { keyPressed: enterPressed } = useKeyPress('Enter');
  const lastQuestionNextButtonContent = useMemo(() => {
    if (caterer) {
      return t('common:labels.next');
    }
    if (mobile) {
      return t('lastQuestionCTAShort');
    }
    return t('lastQuestionCTA');
  }, [caterer, mobile]);

  useEffect(() => {
    if (enterPressed) {
      handleNext();
    }
  }, [enterPressed]);

  if (state.status !== QUALIFICATION_STATUS.answering) {
    return null;
  }
  const { qualification } = state;

  const position = qualification.steps.findIndex(
    (q) => q.id === qualification.step.id
  );

  const nextQuestion = qualification.steps[position + 1];
  const prevQuestion = qualification.steps[position - 1];

  const displayBackButton = !!prevQuestion && !prevQuestion.disabled;

  const handleNext = async () => {
    actions.nextStep();
  };

  const goBack = () => {
    actions.prevStep();
  };

  const hideNextBtn = (() => {
    if (state.status !== 'answering') return false;

    const customerTierABCExperiment =
      isVariationActive(EXPERIMENT.customerTierABC.variations.variationB) ||
      isVariationActive(EXPERIMENT.customerTierABC.variations.variationC);
    if (
      customerTierABCExperiment &&
      state.qualification.step.id === 'rare_customer_tier'
    )
      return true;
    return false;
  })();

  const NextBtn = (() => {
    if (hideNextBtn) return null;
    if (nextQuestion) {
      return (
        <Button
          onClick={handleNext}
          size="lg"
          id="survey-next"
          data-current-id={step.id}
          disabled={!canContinue}
        >
          <Typography component="span" lineHeight={1} variant="h6" mr={0.5}>
            {t('common:labels.next')}
          </Typography>
          <ArrowForward />
        </Button>
      );
    }

    return (
      <Button
        size="lg"
        onClick={handleNext}
        disabled={!canContinue}
        id="searchCaterers"
      >
        <Typography component="span" lineHeight={1} variant="h6">
          {lastQuestionNextButtonContent}
        </Typography>
        <ArrowForward />
      </Button>
    );
  })();

  const errors = errorsOfStep(step.id);

  if (step.id.includes('tier-')) {
    return null;
  }

  return (
    <SurveyControlsLayout
      nextButton={NextBtn}
      prevButton={
        displayBackButton && (
          <Button
            size="lg"
            variant="tertiary"
            onClick={goBack}
            id="survey-back"
          >
            {mobile ? (
              <ArrowBack />
            ) : (
              <>
                <ArrowBack />
                <Typography variant="h6" component="span" lineHeight={1} ml={1}>
                  {t('common:labels.back')}
                </Typography>
              </>
            )}
          </Button>
        )
      }
      answers={
        <Box mr={2} pl={[0, 2, 2, 4]}>
          <SurveySelection
            mode="previous-answers"
            currentStep={step}
            wrap={false}
            additionalPills={
              caterer ? <Pill>{caterer.company_name}</Pill> : undefined
            }
          />
        </Box>
      }
      errors={!!errors?.length && <QuestionErrors errors={errors} />}
      footNote={step.content?.footNote && t(step.content?.footNote)}
    />
  );
}

export default SurveyControls;
